import ErrorBoundary from 'components/ErrorBoundary';
import { LayoutContainer } from 'components/Layout';
import { LoadingSkeletonItem } from 'components/LoadingSkeleton';
import LoadMoreList from 'components/LoadMoreList';
import ProductCard from 'components/ProductCard';
import {
	PRODUCT_LIST_GRID_CLASSES,
	PRODUCT_LIST_ITEM_SEPARATOR_CLASSES,
} from 'components/ProductList';
import Text from 'components/Text';
import type { GTMItemListId, GTMItemListName } from 'hooks/gtm';
import type {
	ProductCard as ProductCardType,
	RelatedProductCard,
} from 'models/productCard';
import { cn } from 'utils/classNames';
import { is, range } from 'utils/helpers';

interface Props {
	className?: string;
	gtmItemListId: GTMItemListId;
	gtmItemListName: GTMItemListName;
	hasLoadMoreButton?: boolean;
	heading: string;
	isLoading: boolean;
	isLoadingMore?: boolean;
	loadMoreButtonText: string;
	onLoadMore?: () => unknown;
	products: (RelatedProductCard | ProductCardType)[];
}

export default function ProductDetailsProductList({
	className,
	gtmItemListId,
	gtmItemListName,
	hasLoadMoreButton,
	heading,
	isLoading,
	isLoadingMore,
	loadMoreButtonText,
	onLoadMore,
	products,
}: Props) {
	if (!isLoading && !is.arrayWithLength(products)) {
		return null;
	}

	return (
		<ErrorBoundary isPageWidth>
			<LayoutContainer outerClassName={className}>
				<Text as="h2" text={heading} />
				<LoadMoreList
					isLoading={isLoadingMore}
					hasLoadMoreButton={hasLoadMoreButton}
					loadedItemFocusTarget="first-interactive"
					onLoadMoreClick={onLoadMore}
					listClassName={cn(PRODUCT_LIST_GRID_CLASSES, 'mt-r-md')}
					buttonText={loadMoreButtonText}
				>
					{isLoading
						? range(4).map((i) => (
								<LoadingSkeletonItem key={i} height="24rem" />
							))
						: products?.map((product, i) => (
								<ProductCard
									className={PRODUCT_LIST_ITEM_SEPARATOR_CLASSES}
									key={product.id}
									product={product}
									productListIndex={i}
									gtmItemListId={gtmItemListId}
									gtmItemListName={gtmItemListName}
									isColumnEvenHeight
								/>
							))}
				</LoadMoreList>
			</LayoutContainer>
		</ErrorBoundary>
	);
}

ProductDetailsProductList.displayName = 'ProductDetailsProductList';
